import {
  FilterItem,
  ToggleSwitch,
  Workplace as WorkplaceComponent,
  WorkplaceListItem,
} from "@waldner-laboreinrichtungen/component-system";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { useDeviceImage } from "../hooks/use-device-image";
import { getIncidentsForDevice } from "../hooks/use-incidents";
import { useUnitStore } from "../store/unit-store";
import type { Class, Device, Incident } from "../view-model";

export type WorkplaceProps = {
  className?: string;
  devices: Device[];
  incidents: Incident[];
};

type FilterMap = {
  [key in Class | "all"]?: {
    value: Class | "all";
    displayName: string;
    count: number;
  };
};

const useFilteredDevices = (
  devices: Device[],
  initialFilter: Class | "all"
) => {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState<Class | "all">(
    initialFilter
  );

  const filteredDevices = devices.filter((device) => {
    if (activeFilter === "all") return true;
    return device.class === activeFilter;
  }) as Device[];

  const deviceCategories: Set<Class> = new Set([
    "workplace",
    "storage",
    "device",
  ]);

  const filterCategories: (Class | "all")[] = [
    "all",
    ...deviceCategories.values(),
  ];

  const filterMap: FilterMap = {};
  for (const filter of filterCategories) {
    filterMap[filter] = {
      value: filter,
      displayName: t(`workplace.${filter}`),
      count: 0,
    };
  }

  if (devices) {
    for (const device of devices) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      filterMap["all"]!.count = filterMap["all"]!.count + 1;
      if (device.class && filterMap[device.class as Class]) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        filterMap[device.class as Class]!.count =
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          filterMap[device.class as Class]!.count + 1;
      }
    }
  }

  const onChangeFilteredDevices = (filterValue: string) => {
    setActiveFilter(filterValue as Class | "all");
  };

  const filters = Object.entries(filterMap).map(([, filter]) => filter);

  return {
    filteredDevices,
    onChangeFilteredDevices,
    filters,
  };
};

const useDevicesWithIncidents = (
  devices: Device[],
  incidents: Incident[],
  toggleErrors: boolean
) => {
  // Mount-aware: resolve each device's view-units so incidents on mounted
  // FUs/functions (SOURCE browsePath) are attributed to the host device.
  const getUnits = useUnitStore((s) => s.getUnitsForDevice);
  const devicesWithIncidents = [];
  for (const device of devices) {
    const deviceIncidents = getIncidentsForDevice(
      device,
      incidents,
      getUnits(device.id)
    );
    const errorCount = deviceIncidents.errors.length;
    const warningCount = deviceIncidents.warnings.length;
    if (toggleErrors && errorCount > 0) {
      devicesWithIncidents.push({ ...device, errorCount, warningCount });
    }
    if (!toggleErrors) {
      devicesWithIncidents.push({ ...device, errorCount, warningCount });
    }
  }
  return devicesWithIncidents;
};

const WorkplaceListItemWithImageFallback: FC<
  {
    imageUrl: string;
    deviceTypeImage?: string[];
  } & Omit<React.ComponentProps<typeof WorkplaceListItem>, "imageUrl">
> = ({ deviceTypeImage, imageUrl, ...props }) => {
  // Prefer the OPC-UA DeviceTypeImage (like device-detail), fall back to the
  // static device.image.
  const resolvedImageUrl = useDeviceImage(deviceTypeImage, imageUrl);
  const [currentImageUrl, setCurrentImageUrl] = useState(resolvedImageUrl);

  useEffect(() => {
    // Check if image exists
    const img = new Image();

    const handleLoad = () => {
      setCurrentImageUrl(resolvedImageUrl);
    };

    const handleError = () => {
      setCurrentImageUrl("/devices/no-image.png");
    };

    img.addEventListener("load", handleLoad);
    img.addEventListener("error", handleError);
    img.src = resolvedImageUrl;

    return () => {
      img.removeEventListener("load", handleLoad);
      img.removeEventListener("error", handleError);
    };
  }, [resolvedImageUrl]);

  return <WorkplaceListItem {...props} imageUrl={currentImageUrl} />;
};

export const Workplace: FC<WorkplaceProps> = ({ devices, incidents }) => {
  const { t } = useTranslation();

  const { state } = useLocation();
  const [toggleErrors, setToggleErrors] = useState(state?.hasErrors || false);
  const activeCategory = state?.activeCategory || "all";

  const { filteredDevices, filters, onChangeFilteredDevices } =
    useFilteredDevices(devices, activeCategory);

  const devicesWithIncidents = useDevicesWithIncidents(
    filteredDevices,
    incidents,
    toggleErrors
  );

  return (
    <WorkplaceComponent
      action={
        <ToggleSwitch
          checked={toggleErrors}
          isLabelFirst
          onChange={() => setToggleErrors(!toggleErrors)}
        >
          {t("show errors")}
        </ToggleSwitch>
      }
      filters={filters.map((filter) => (
        <FilterItem
          defaultChecked={filter.value === activeCategory}
          id={filter.value}
          key={filter.value}
          name="workplaceFilter"
          onChange={(e) => onChangeFilteredDevices(e.target.value)}
          value={filter.value}
        >
          {`${filter.displayName} (${filter.count})`}
        </FilterItem>
      ))}
      workplaces={devicesWithIncidents?.map((device) => (
        <WorkplaceListItemWithImageFallback
          actionText={t("more details")}
          deviceTypeImage={device.deviceTypeImage}
          errorText={
            device.errorCount
              ? `${device.errorCount} ${t("incident.type.critical")}`
              : undefined
          }
          imageUrl={device.image}
          infoText={
            [
              device.state?.operation &&
                (device.state.operation === "low" ||
                  device.state.operation === "off") &&
                t(`state.operation.${device.state.operation}`),
              device.state?.status === "offline" && t("device.offline"),
              ...(device.state?.messages || []).map((message) =>
                t(message.message)
              ),
            ].filter(Boolean) as string[]
          }
          key={device.id}
          location={device.location.position || ""}
          modelText={device.name || t("data.missing.name")}
          to={`/workplaces/${device.id}`}
          warningText={
            device.warningCount
              ? `${device.warningCount} ${t("incident.type.warning")}`
              : undefined
          }
        />
      ))}
    />
  );
};
